import { useMemo, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isUndefined } from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { LoadingSection } from 'components';
import { MetricCardsRow } from 'dashboard409a/components';
import { useMetricsCatalog } from 'dashboard409a/services/alexandria/hooks';
import { MetricUnitsByKey } from 'dashboard409a/services/alexandria/types';
import { RootStateType } from 'dashboard409a/states/store';
import { SubscriptionExpiredBox } from '../SubscriptionExpiredBox';
import { buildMedians } from './compute/medians';
import { CompRef, DEFAULT_RANGE_KEY } from './constants';
import CurrentValueBars from './CurrentValueBars';
import {
  COMP_SERIES_QUERY_KEY,
  compSetMissingTickerLabels,
  compSetToRefs,
  useCompSet,
  useCompsSeries,
  usePrefetchCompRanges,
} from './hooks';
import MultiplesChart from './MultiplesChart';
import PerformanceTable from './PerformanceTable';
import RateOfChangesChart from './RateOfChangesChart';

interface PublicCompsProps {
  // Overrides the measurement date's configured comp set (GPCComparison).
  comps?: CompRef[];
}

// Redesigned Public Comps section, fed by Alexandria metric series over the
// Django GraphQL proxy. The time-series charts manage their own ranges; cards,
// bars, and the table share the default recent window fetched here.
const PublicComps = ({ comps }: PublicCompsProps) => {
  const { currentCompany, currentCompanyMeasurementDateId } = useSelector((state: RootStateType) => state.global);
  const compSet = useCompSet(comps ? null : currentCompanyMeasurementDateId);
  const effectiveComps = useMemo(() => comps ?? compSetToRefs(compSet.data), [comps, compSet.data]);
  const compsMissingTickers = useMemo(
    () => (comps ? [] : compSetMissingTickerLabels(compSet.data)),
    [comps, compSet.data]
  );
  // The charts defer expensive calculations. Remount them when the comp
  // universe changes so a cached report switch cannot defer the old report's
  // series into the first render of the new one.
  const compsIdentity = useMemo(() => JSON.stringify(effectiveComps.map(comp => comp.ticker)), [effectiveComps]);
  const {
    byCompany,
    companyNames,
    hasData: hasSeriesData,
    isLoading: isSeriesLoading,
    isError: isSeriesError,
    missingTickers,
    dataUpdatedAt,
  } = useCompsSeries(effectiveComps, DEFAULT_RANGE_KEY);
  const medians = useMemo(() => buildMedians(byCompany), [byCompany]);

  // Manual escape hatch for data updated outside the app's knowledge — e.g.
  // an analyst-triggered backfill that changes values for the same tickers,
  // which the ticker-keyed cache cannot detect on its own.
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = async () => {
    if (isRefreshing) {
      return;
    }
    setIsRefreshing(true);
    try {
      const refreshes: Promise<void>[] = [
        queryClient.invalidateQueries(
          { queryKey: COMP_SERIES_QUERY_KEY, refetchType: 'active' },
          { cancelRefetch: false }
        ),
      ];
      if (!comps && currentCompanyMeasurementDateId) {
        refreshes.push(
          queryClient.invalidateQueries(
            {
              queryKey: ['gpc-comp-set', 'v2', currentCompanyMeasurementDateId],
              exact: true,
              refetchType: 'active',
            },
            { cancelRefetch: false }
          )
        );
      }
      await Promise.all(refreshes);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Once the default view has data, warm the other ranges in the background
  // so the charts' range switches resolve from cache instead of ~4s fetches.
  usePrefetchCompRanges(effectiveComps, !isSeriesLoading && !isSeriesError && effectiveComps.length > 0);

  // Unit semantics (percent vs currency, scale factor) come from the catalog
  // instead of being hardcoded per metric — bars and table wait for it.
  const catalog = useMetricsCatalog();
  const metricUnits: MetricUnitsByKey = useMemo(
    () =>
      Object.fromEntries(
        (catalog.data ?? []).map(entry => [
          entry.metric_key,
          { unit: entry.unit, factor: entry.factor !== null ? parseFloat(entry.factor) : null },
        ])
      ),
    [catalog.data]
  );
  const isLoading = isSeriesLoading || catalog.isLoading;
  const hasCatalogData = catalog.data !== undefined;
  const hasCompSetData = comps !== undefined || compSet.data !== undefined;
  const hasRefreshError =
    (isSeriesError && hasSeriesData) ||
    (catalog.isError && hasCatalogData) ||
    (!comps && compSet.isError && hasCompSetData);
  const hasFatalError =
    (isSeriesError && !hasSeriesData) ||
    (catalog.isError && !hasCatalogData) ||
    (!comps && compSet.isError && !hasCompSetData);

  // Same paywall rule as the legacy GPCComp section.
  const subscriptionIsActive = currentCompany?.is_active;
  if (!isUndefined(subscriptionIsActive) && subscriptionIsActive === false) {
    return <SubscriptionExpiredBox />;
  }

  if (hasFatalError) {
    return (
      <Alert
        action={
          <Button color="inherit" disabled={isRefreshing} onClick={handleRefresh} size="small">
            {isRefreshing ? 'Retrying…' : 'Retry'}
          </Button>
        }
        severity="error"
        sx={{ my: 2 }}>
        Market data is unavailable right now. Try again in a few minutes.
      </Alert>
    );
  }

  // isPending (not isLoading): the query is disabled until the measurement
  // date id lands in the store, and a disabled query is never "loading" —
  // without this the empty state flashes before the comp set is fetched.
  if (!comps && compSet.isPending) {
    return <LoadingSection />;
  }

  if (!effectiveComps.length) {
    if (compsMissingTickers.length) {
      return (
        <Alert severity="warning">
          {`Market data is unavailable because the configured comparable companies do not have ticker symbols: ${compsMissingTickers.join(
            ', '
          )}.`}
        </Alert>
      );
    }
    return <Alert severity="info">No comparable companies are configured for this measurement date yet.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {compsMissingTickers.length > 0 && (
        <Alert severity="warning">
          {`Market data excludes ${compsMissingTickers.join(', ')} because no ticker symbol is configured.`}
        </Alert>
      )}
      {hasRefreshError && (
        <Alert severity="warning">
          The latest market-data refresh failed. Showing the most recently available data.
        </Alert>
      )}
      {!isLoading && missingTickers.length > 0 && (
        <Alert severity="warning">
          {`No data for ${missingTickers.join(', ')} — statistics are computed from the remaining ${
            Object.keys(byCompany).length
          } companies.`}
        </Alert>
      )}
      <Box>
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography color="primary" fontWeight={600} variant="subtitle1">
            State of Market Statistic
          </Typography>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
            {dataUpdatedAt > 0 && (
              <Typography color="text.secondary" variant="caption">
                {`Dashboard data fetched at ${moment(dataUpdatedAt).format('h:mm A')}`}
              </Typography>
            )}
            <Tooltip title={isRefreshing ? 'Refreshing…' : 'Refresh market data'}>
              <span>
                <IconButton
                  aria-label="Refresh market data"
                  disabled={isRefreshing}
                  onClick={handleRefresh}
                  size="small">
                  <RefreshIcon
                    fontSize="small"
                    sx={{
                      animation: isRefreshing ? 'refresh-spin 1s linear infinite' : undefined,
                      '@keyframes refresh-spin': {
                        from: { transform: 'rotate(0deg)' },
                        to: { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        {isLoading ? <LoadingSection /> : <MetricCardsRow medians={medians} />}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 460px', minWidth: 380 }}>
          <MultiplesChart key={`multiples-${compsIdentity}`} comps={effectiveComps} />
        </Box>
        <Box sx={{ flex: '1 1 460px', minWidth: 380 }}>
          <RateOfChangesChart key={`rate-of-changes-${compsIdentity}`} comps={effectiveComps} />
        </Box>
      </Box>
      <CurrentValueBars
        comps={effectiveComps}
        byCompany={byCompany}
        companyNames={companyNames}
        isLoading={isLoading}
        metricUnits={metricUnits}
      />
      <PerformanceTable
        comps={effectiveComps}
        byCompany={byCompany}
        companyNames={companyNames}
        isLoading={isLoading}
        metricUnits={metricUnits}
      />
    </Box>
  );
};

export default PublicComps;
